import { CircularProgress, SvgIcon } from '@material-ui/core';
import Modal from 'components/UI/Modal';
import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Button from 'components/FormUI/Button';
import * as R from 'ramda';
import * as contentService from 'services/content.service';

import './UploadThumbnail.scss';
import { mdiDotsVertical } from '@mdi/js';
import ClickDropDown, { StyledDropDownItem } from 'components/UI/ClickDropDown';
import axiosInstance from 'utils/axiosInstance';
import PropTypes from 'prop-types';
import { useContribution } from 'pages/ContributionView/hooks';
import { getThemedColors } from 'services/contributions.service';

const StyledTitle = styled.p`
  font-family: Avenir;
  font-weight: 800;
  size: 20px;
  color: ${({ textColor }) => textColor || '#000000de'};
  margin-bottom: 16px;
  letter-spacing: 0.25px;
  font-style: normal;
  line-height: normal;
  letter-spacing: 0.25px;
  text-align: center;
`;

const ContentContainer = styled.div`
  min-height: 250px;
  min-width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  align-items: center;
  padding: ${props => (props.noPadding ? '0' : '0px 10px')};
  width: 155px;
  height: 90px;
  border-radius: 4px;
  border: 1px dashed ${({ isDarkModeEnabled, darkBorderColor }) => (isDarkModeEnabled ? darkBorderColor : '#dfe3e4')};
  ${({ isDarkModeEnabled }) =>
    !isDarkModeEnabled &&
    css`
      background: #fff;
    `};
  cursor: pointer;
  margin: 0 auto 16px auto;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: cover;
  }

  .actions {
    position: absolute;
    top: 2px;
    right: 0;
  }
`;

const StyledSubtitle = styled.div`
  color: #a9a9a9;
  font-family: Avenir;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 500;
  line-height: 1.3125rem; /* 150% */
  letter-spacing: 0.00938rem;
`;
const StyledMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
const StyledMessage = styled.div`
  color: #a9a9a9;
  text-align: center;
  font-family: Avenir;
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const StyledBannertext = styled.div`
  color: ${({ isError = false }) => (isError ? '#8B0000' : '#bdbdbd')};
  text-align: center;
  font-family: -apple-system, Avenir;
  font-size: 1rem;
  font-style: normal;
  font-weight: 350;
  line-height: 1.25rem; /* 125% */
  padding-left: 2rem;
  padding-right: 2rem;
  padding-bottom: 1rem;

  ${({ disabled }) =>
    disabled &&
    css`
      color: rgba(0, 0, 0, 0.12);
    `}
`;

const FILE_SIZE_LIMIT = 10 * 1024 * 1024;

function UploadThumbnail({
  title,
  isOpen,
  setIsOpen,
  thumbnailValue,
  setFieldValue,
  fieldName,
  isPreviewMode = false,
  updateSessionthumbnail,
  modalProps = {},
}) {
  const uploadFileRef = useRef(null);
  const [disabled, setDisabled] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [thumbnail, setThumbnail] = useState(thumbnailValue);
  const [progress, setProgress] = useState(0);
  const isFilesSizeLimitOrLess = R.compose(sum => sum <= FILE_SIZE_LIMIT, R.sum, R.map(R.prop('size')));
  const guid = () => {
    let d = new Date().getTime();
    const guid = 'xxxx-xxxx-xxxx-xxxx'.replace(/[xy]/g, function (c) {
      const r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x7) | 0x8).toString(16);
    });
    return guid;
  };
  const contribution = useContribution();
  const { themedColor, themedCardOutlineColor } = getThemedColors(contribution);
  const { isDarkModeEnabled } = contribution;
  const handleUpdateProgress = (partNumber, totalParts) => progressData => {
    const percentPerChunk = 100 / totalParts;
    setProgress((progressData / 100) * percentPerChunk + percentPerChunk * (partNumber - 1));
  };
  const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState('');
  const [fileUploadError, setfileUploadError] = useState(false);

  const handleIsFileLoad = () => {
    setUploading(false);
    setDisabled(false);
  };

  const uploadPartFile = (chunk, partNumber, totalParts, isLastPart, documentId, fileName, uploadId, prevETags) =>
    new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', chunk);
      contentService
        .addPublicFile(
          formData,
          partNumber,
          isLastPart,
          documentId,
          fileName,
          uploadId,
          prevETags,
          handleUpdateProgress(partNumber, totalParts),
        )
        .then(result => {
          resolve(result);
        })
        .then(() => {
          if (isLastPart) {
            handleIsFileLoad();
          }
        })
        .catch(result => {
          handleIsFileLoad();
          reject(result);
        });
    });

  const handleUploadFile = async ({ target: { files } }) => {
    setFileUploadErrorMessage('');
    setfileUploadError(false);
    if (files.length === 0) {
      return;
    }

    const chunkSize = FILE_SIZE_LIMIT || 5000000;
    if (files[0].size > FILE_SIZE_LIMIT) {
      setFileUploadErrorMessage('Selected file size is greater than 10MB.');
      setfileUploadError(true);
      return;
    }

    if (isFilesSizeLimitOrLess(files)) {
      setDisabled(true);
      setUploading(true);
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const documentId = guid();
        const fileName = file.name;
        let uploadId = '';
        let prevETags = '';
        let partNumber = 1;
        for (let start = 0; start < file.size; start += chunkSize) {
          const isLastPart = start + chunkSize >= file.size;
          const chunk = file.slice(start, start + chunkSize);
          const totalParts = Math.ceil(file.size / chunkSize);
          const result = await uploadPartFile(
            chunk,
            partNumber,
            totalParts,
            isLastPart,
            documentId,
            fileName,
            uploadId,
            prevETags,
          );

          if (result) {
            if (isLastPart) {
              setThumbnail(result);
            }
            uploadId = result.uploadId;
            prevETags = result.prevETags;
          }
          partNumber++;
        }
      }
      setUploading(false);
    }
  };
  const onCloseModal = () => {};
  const onSubmit = () => {};

  const CONTRIBUTION_COLORS = {
    PrimaryColorCode: '#CDBA8F',
    AccentColorCode: '#116582',
    TertiaryColorCode: '#F6E8BO',
  };
  const btnColor = CONTRIBUTION_COLORS.PrimaryColorCode;

  const getActionDropdown = () => {
    return (
      <ClickDropDown className="resource-card-dropdown-icon" icon={mdiDotsVertical}>
        <div>
          <StyledDropDownItem
            style={{ color: 'black', textDecoration: 'none' }}
            onClick={e2 => {
              e2.preventDefault();
              uploadFileRef.current.click();
            }}
          >
            Change Thumbnail
          </StyledDropDownItem>
        </div>
        <div onClick={() => {}} onKeyUp={() => {}} role="button" tabIndex="0">
          <StyledDropDownItem
            style={{ color: 'black', textDecoration: 'none' }}
            onClick={e2 => {
              e2.preventDefault();
              setThumbnail(null);
            }}
          >
            Delete
          </StyledDropDownItem>
        </div>
      </ClickDropDown>
    );
  };

  // const updateSessionthumbnail =()=>{
  //   console.info("video thumbnail",thumbnail)
  //   console.info("video", contribution);
  //   //sessions[${sessionIndex}].sessionTimes[${sessionTimeIndex}].selfPacedThumbnailUrl
  // }
  return (
    <Modal
      className="add-thumbnail-popup"
      isOpen={isOpen}
      // applyTheming
      isCreatingContribution
      hiddenCancel
      onCancel={onCloseModal}
      onSubmit={onSubmit}
      disableSubmitClick={disabled}
      disableOverlayClick
      CrossIconHide
      disableFooter
      {...modalProps}
    >
      <>
        {title && <StyledTitle {...(isDarkModeEnabled && { textColor: themedColor })}>{title}</StyledTitle>}
        {!uploading && !thumbnail && (
          <ContentContainer
            onClick={() => uploadFileRef.current.click()}
            {...{ isDarkModeEnabled, darkBorderColor: themedCardOutlineColor }}
          >
            <SvgIcon style={{ height: '24px', width: '33px' }}>
              <svg width="29" height="24" viewBox="0 0 29 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_29631_183)">
                  <path
                    d="M28.202 4.73189C28.202 5.08871 28.202 5.44554 28.202 5.80236C28.1877 5.85232 28.1663 5.90227 28.152 5.95223C27.8166 8.04322 26.6605 9.46338 24.6908 10.227C24.4553 10.3198 24.3982 10.4268 24.3982 10.6623C24.4053 14.5017 24.4053 18.334 24.4053 22.1735C24.4053 23.2939 23.706 24.0004 22.5927 24.0004C15.9343 24.0004 9.26886 24.0004 2.61052 24.0004C1.68991 24.0004 1.10472 23.5508 0.84067 22.673C0.833534 22.6373 0.812125 22.6088 0.797852 22.5731C0.797852 16.0218 0.797852 9.46338 0.797852 2.91208C1.12613 1.85588 1.87546 1.52047 2.9388 1.52047C8.26975 1.54188 13.6007 1.52761 18.9317 1.53474C19.1101 1.53474 19.2457 1.48479 19.3813 1.36347C22.2858 -1.26276 26.9459 0.0788984 28.0021 3.8541C28.0806 4.13956 28.1377 4.43929 28.202 4.73189ZM22.1859 10.5196C22.1859 10.648 22.1859 10.7622 22.1859 10.8835C22.1859 14.3091 22.1859 17.7417 22.1859 21.1672C22.1859 21.6882 22.1003 21.7738 21.5864 21.7738C15.5989 21.7738 9.61855 21.7738 3.63104 21.7738C3.11007 21.7738 3.02444 21.6953 3.02444 21.1744C3.02444 16.7997 3.02444 12.4322 3.02444 8.05749C3.02444 7.50084 3.08866 7.43662 3.63817 7.43662C8.34112 7.43662 13.0369 7.43662 17.7399 7.43662C17.8469 7.43662 17.9611 7.43662 18.0967 7.43662C17.3688 5.62395 17.5258 3.92546 18.5534 2.28407C18.4178 2.28407 18.3393 2.28407 18.2608 2.28407C13.0583 2.28407 7.86297 2.28407 2.66047 2.28407C1.91114 2.28407 1.54718 2.64803 1.54718 3.39736C1.54718 9.64893 1.54718 15.8933 1.54718 22.1449C1.54718 22.88 1.91114 23.2511 2.6462 23.2511C9.28313 23.2511 15.9201 23.2511 22.557 23.2511C23.2921 23.2511 23.656 22.8871 23.656 22.152C23.656 18.3697 23.656 14.5874 23.656 10.7979C23.656 10.7051 23.656 10.6052 23.656 10.5196C23.1565 10.5196 22.6855 10.5196 22.1859 10.5196ZM3.80231 8.18595C3.80231 12.2823 3.80231 16.3358 3.80231 20.4678C3.90936 20.3394 3.95931 20.268 4.02354 20.2038C6.30008 17.8202 8.57662 15.4366 10.8532 13.0459C11.1529 12.7319 11.3384 12.739 11.6168 13.0744C12.673 14.3376 13.7292 15.6079 14.7854 16.871C14.8425 16.9424 14.9067 17.0066 14.978 17.0923C15.5204 16.5285 16.0342 15.9861 16.5481 15.4438C16.8692 15.1012 17.0405 15.1155 17.3474 15.4794C18.6319 17.0209 19.9165 18.5553 21.2011 20.0967C21.2582 20.1681 21.3224 20.2323 21.4152 20.3465C21.4223 20.2252 21.4294 20.1681 21.4294 20.1181C21.4294 16.9281 21.4294 13.7453 21.4366 10.5553C21.4366 10.384 21.3795 10.3126 21.2153 10.2555C20.1805 9.90584 19.3313 9.2921 18.689 8.41431C18.5606 8.24304 18.4321 8.18595 18.2251 8.18595C13.5222 8.19308 8.8264 8.19308 4.12345 8.19308C4.01641 8.18595 3.9165 8.18595 3.80231 8.18595ZM22.921 0.742592C20.4303 0.742592 18.3964 2.76222 18.3893 5.25999C18.3822 7.75062 20.4089 9.79166 22.8995 9.79879C25.3902 9.80593 27.4384 7.77917 27.4455 5.29567C27.4598 2.79076 25.4259 0.749728 22.921 0.742592ZM4.28759 21.0102C8.62658 21.0102 12.9227 21.0102 17.2617 21.0102C15.2278 18.5767 13.2225 16.1717 11.21 13.7595C8.89776 16.1788 6.61409 18.5767 4.28759 21.0102ZM16.9263 16.136C16.4196 16.6641 15.9415 17.1636 15.4562 17.6775C16.3697 18.7694 17.2689 19.847 18.1752 20.9246C18.2323 20.9888 18.3607 21.0173 18.4535 21.0245C19.1386 21.0316 19.8237 21.0316 20.516 21.0316C20.6587 21.0316 20.8086 21.0316 21.0084 21.0316C19.6239 19.3688 18.2822 17.7631 16.9263 16.136Z"
                    fill="#A9A9A9"
                  />
                  <path
                    d="M9.21172 11.5258C9.21172 12.5107 8.41957 13.3099 7.43474 13.3171C6.4499 13.3242 5.62207 12.5035 5.62207 11.5187C5.62207 10.5267 6.44277 9.72029 7.43474 9.73456C8.42671 9.7417 9.21172 10.5338 9.21172 11.5258ZM8.46239 11.5258C8.46239 10.9478 8.01279 10.491 7.43474 10.4839C6.84955 10.4768 6.3714 10.9478 6.3714 11.5187C6.3714 12.0896 6.85668 12.5749 7.4276 12.5677C8.00566 12.5606 8.46239 12.0967 8.46239 11.5258Z"
                    fill="#A9A9A9"
                  />
                  <path
                    d="M23.292 3.48309C23.292 4.4822 23.292 5.4599 23.292 6.4376C23.292 6.53751 23.3205 6.65883 23.2777 6.72306C23.1992 6.84438 23.0708 7.00138 22.9566 7.00852C22.8353 7.02279 22.6854 6.89433 22.5855 6.78728C22.5284 6.72306 22.5427 6.58746 22.5427 6.48042C22.5427 5.55267 22.5427 4.63207 22.5427 3.70432C22.5427 3.63296 22.5355 3.56159 22.5284 3.426C22.2429 3.71146 21.9932 3.9541 21.7505 4.20388C21.6649 4.28952 21.5792 4.38229 21.4865 4.46793C21.3152 4.61779 21.094 4.61066 20.9584 4.46079C20.8156 4.29665 20.8156 4.1111 20.9584 3.9541C21.1011 3.78996 21.2724 3.63296 21.4294 3.47596C21.8147 3.09059 22.2001 2.70521 22.5855 2.32698C22.8353 2.0772 23.0208 2.0772 23.2706 2.32698C23.7916 2.84081 24.3054 3.35464 24.8192 3.8756C25.0404 4.09683 25.0547 4.31092 24.8834 4.4822C24.7193 4.6392 24.5052 4.61779 24.2982 4.41084C23.9914 4.1111 23.6916 3.81137 23.3848 3.51164C23.3634 3.49023 23.342 3.49023 23.292 3.48309Z"
                    fill="#A9A9A9"
                  />
                  <path
                    d="M22.9352 7.6582C23.5489 7.6582 24.1627 7.6582 24.7835 7.6582C25.069 7.6582 25.2403 7.80093 25.2403 8.0293C25.2403 8.25767 25.069 8.4004 24.7764 8.4004C23.5347 8.4004 22.3 8.4004 21.0583 8.4004C20.7728 8.4004 20.6016 8.25053 20.6016 8.02216C20.6016 7.7938 20.7657 7.6582 21.0583 7.6582C21.6863 7.6582 22.3143 7.6582 22.9352 7.6582Z"
                    fill="#A9A9A9"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_29631_183">
                    <rect width="27.4041" height="24" fill="white" transform="translate(0.797852)" />
                  </clipPath>
                </defs>
              </svg>
            </SvgIcon>
            <StyledSubtitle>Upload Thumbnail</StyledSubtitle>
            <StyledMessageContainer>
              <StyledMessage>For best results, upload image of size 16:9</StyledMessage>
              <StyledMessage>(1920x1080) format and not exceed 5 MB.</StyledMessage>
            </StyledMessageContainer>
          </ContentContainer>
        )}
        {uploading && (
          <ContentContainer {...{ isDarkModeEnabled, darkBorderColor: themedCardOutlineColor }}>
            <CircularProgress color="#00000026" value={progress} />
            <StyledSubtitle>Uploading</StyledSubtitle>
          </ContentContainer>
        )}
        {!uploading && thumbnail && (
          <ContentContainer noPadding {...{ isDarkModeEnabled, darkBorderColor: themedCardOutlineColor }}>
            <img src={thumbnail} alt="thumbnail" />
            <div className="actions">{getActionDropdown()}</div>
          </ContentContainer>
        )}
        <input
          id="file-upload"
          ref={uploadFileRef}
          type="file"
          accept="image/*"
          className="d-none"
          onChange={handleUploadFile}
          disabled={disabled}
          multiple
        />
        {fileUploadError && fileUploadErrorMessage && (
          <StyledBannertext isError>{fileUploadErrorMessage}</StyledBannertext>
        )}
        <div className="footer-buttons">
          <Button
            type="button"
            className="cancel-upload"
            disabled={disabled}
            onClick={() => setIsOpen(false)}
            style={
              modalProps?.applyTheming && isDarkModeEnabled
                ? { backgroundColor: 'transparent', color: 'white', borderColor: 'white' }
                : {}
            }
          >
            Cancel
          </Button>
          <Button
            backgroundColor={btnColor}
            className="submit-thumbnail"
            disabled={disabled}
            onClick={() => {
              setUploading(true);
              if (thumbnail) {
                if (isPreviewMode) {
                  updateSessionthumbnail(thumbnail);
                } else {
                  setFieldValue(fieldName, thumbnail);
                }
              } else {
                axiosInstance.post(`/Content/DeletePublicImage?imageLink=${thumbnail}`);
                if (isPreviewMode) {
                  updateSessionthumbnail(null);
                } else {
                  setFieldValue(fieldName, null);
                }
              }
              setUploading(false);
              setIsOpen(false);
            }}
            type="button"
          >
            Submit
          </Button>
        </div>
      </>
    </Modal>
  );
}

UploadThumbnail.propTypes = {
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  thumbnailValue: PropTypes.string.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  fieldName: PropTypes.string.isRequired,
};

export default UploadThumbnail;
